import React from "react";
import { AbsoluteFill, Easing, interpolate, useCurrentFrame } from "remotion";
import {
  CAPTURE_FRAME,
  CURSOR_ENTER_END_FRAME,
  CURSOR_ENTER_START_FRAME,
  DRAG_END_FRAME,
  DRAG_START_FRAME,
  SELECTION_END_FRAME,
  SELECTION_START_FRAME,
} from "./helpers/timeline";

// Screen-space coordinates (1280×720) tuned to the 3D phone+whiteboard
// layout in PhotoParsingScene.tsx. Retune these if the camera or
// settled positions change. The whiteboard "text region" is the
// rectangle the cursor will drag a selection across; the phone "screen
// center" is the drop point.
const CURSOR_OFFSCREEN = { x: 0, y: 720 };
// Tracks the whiteboard's projected outer corners (TL / BR) in screen
// space. The cursor drag spans the board edge-to-edge so the
// "screenshot" reads as capturing the whole sheet. TL nudged inward
// (was 285,165) — previous value started ~25px outside the board's
// upper-left corner.
const WHITEBOARD_TEXT_TL = { x: 285, y: 335 };
const WHITEBOARD_TEXT_BR = { x: 600, y: 545 };
const PHONE_SCREEN_CENTER = { x: 750, y: 280 };

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

// macOS-style arrow cursor with drop shadow.
const Cursor: React.FC<{ x: number; y: number; rotationDeg?: number }> = ({
  x,
  y,
  rotationDeg = 0,
}) => (
  <svg
    width={36}
    height={48}
    viewBox="0 0 36 48"
    style={{
      position: "absolute",
      left: x - 5,
      top: y - 3,
      transform: `rotate(${rotationDeg}deg)`,
      transformOrigin: "5px 3px",
      filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.35))",
    }}
  >
    <path
      d="M4 2 L4 36 L14 27 L20 41 L25 39 L19 25 L32 25 Z"
      fill="white"
      stroke="black"
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
  </svg>
);

// Mini workout card used for the screenshot thumbnail being dragged. Mirrors
// the whiteboard content so it reads as a screenshot of the workout.
const ScreenshotCard: React.FC<{
  width: number;
  height: number;
  opacity: number;
  left: number;
  top: number;
}> = ({ width, height, opacity, left, top }) => {
  const fontFamily = '"Permanent Marker", "Marker Felt", sans-serif';
  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width,
        height,
        backgroundColor: "#f5f5f0",
        border: "1px solid rgba(0,0,0,0.25)",
        borderRadius: 6,
        boxShadow: "0 8px 22px rgba(0,0,0,0.28)",
        opacity,
        overflow: "hidden",
        padding: `${height * 0.07}px ${width * 0.07}px`,
        fontFamily,
        color: "#1c1c1c",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: height * 0.04,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: height * 0.13,
          letterSpacing: 0.5,
        }}
      >
        AMRAP — 10 MIN
      </div>
      <div
        style={{
          height: 2,
          background: "#d6336c",
          width: "85%",
          borderRadius: 2,
        }}
      />
      <div style={{ fontSize: height * 0.1, color: "#d6336c" }}>
        10{" "}
        <span style={{ color: "#2b2b2b" }}>PUSH-UPS</span>
      </div>
      <div style={{ fontSize: height * 0.1, color: "#d6336c" }}>
        15 <span style={{ color: "#2b2b2b" }}>AIR SQUATS</span>
      </div>
      <div style={{ fontSize: height * 0.1, color: "#d6336c" }}>
        20 <span style={{ color: "#2b2b2b" }}>SIT-UPS</span>
      </div>
      <div style={{ fontSize: height * 0.1, color: "#d6336c" }}>
        200m <span style={{ color: "#2b2b2b" }}>RUN</span>
      </div>
    </div>
  );
};

export const ScreenshotOverlay: React.FC = () => {
  const frame = useCurrentFrame();

  const cursorEnterProgress = interpolate(
    frame,
    [CURSOR_ENTER_START_FRAME, CURSOR_ENTER_END_FRAME],
    [0, 1],
    {
      easing: Easing.out(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const selectionProgress = interpolate(
    frame,
    [SELECTION_START_FRAME, SELECTION_END_FRAME],
    [0, 1],
    {
      easing: Easing.inOut(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const dragProgress = interpolate(
    frame,
    [DRAG_START_FRAME, DRAG_END_FRAME],
    [0, 1],
    {
      easing: Easing.inOut(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  // Cursor position by phase.
  let cursorX: number;
  let cursorY: number;
  if (frame < CURSOR_ENTER_START_FRAME) {
    cursorX = CURSOR_OFFSCREEN.x;
    cursorY = CURSOR_OFFSCREEN.y;
  } else if (frame < CURSOR_ENTER_END_FRAME) {
    cursorX = lerp(
      CURSOR_OFFSCREEN.x,
      WHITEBOARD_TEXT_TL.x,
      cursorEnterProgress,
    );
    cursorY = lerp(
      CURSOR_OFFSCREEN.y,
      WHITEBOARD_TEXT_TL.y,
      cursorEnterProgress,
    );
  } else if (frame < SELECTION_END_FRAME) {
    cursorX = lerp(
      WHITEBOARD_TEXT_TL.x,
      WHITEBOARD_TEXT_BR.x,
      selectionProgress,
    );
    cursorY = lerp(
      WHITEBOARD_TEXT_TL.y,
      WHITEBOARD_TEXT_BR.y,
      selectionProgress,
    );
  } else if (frame < DRAG_START_FRAME) {
    cursorX = WHITEBOARD_TEXT_BR.x;
    cursorY = WHITEBOARD_TEXT_BR.y;
  } else {
    cursorX = lerp(WHITEBOARD_TEXT_BR.x, PHONE_SCREEN_CENTER.x, dragProgress);
    cursorY = lerp(WHITEBOARD_TEXT_BR.y, PHONE_SCREEN_CENTER.y, dragProgress);
  }

  // Selection rectangle visible while dragging selection.
  const selectionVisible =
    frame >= SELECTION_START_FRAME && frame < CAPTURE_FRAME;
  const selX2 = lerp(
    WHITEBOARD_TEXT_TL.x,
    WHITEBOARD_TEXT_BR.x,
    selectionProgress,
  );
  const selY2 = lerp(
    WHITEBOARD_TEXT_TL.y,
    WHITEBOARD_TEXT_BR.y,
    selectionProgress,
  );

  // Brief white flash on capture — the camera-snap moment.
  const captureFlash = interpolate(
    frame,
    [CAPTURE_FRAME - 2, CAPTURE_FRAME, CAPTURE_FRAME + 8],
    [0, 0.85, 0],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  // Screenshot thumbnail visible from capture through drag end.
  const screenshotVisible = frame >= CAPTURE_FRAME && frame <= DRAG_END_FRAME;
  // Shrinks as it lifts off the whiteboard, fades as it lands on the phone.
  const screenshotScale = interpolate(
    frame,
    [CAPTURE_FRAME, DRAG_START_FRAME, DRAG_END_FRAME - 6],
    [0.95, 0.55, 0.4],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );
  const screenshotOpacity = interpolate(
    frame,
    [DRAG_END_FRAME - 6, DRAG_END_FRAME],
    [1, 0],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const cardW =
    (WHITEBOARD_TEXT_BR.x - WHITEBOARD_TEXT_TL.x) * screenshotScale;
  const cardH =
    (WHITEBOARD_TEXT_BR.y - WHITEBOARD_TEXT_TL.y) * screenshotScale;

  // Card "lift-off" — at CAPTURE the card sits centered on the selection.
  // Through DRAG_START it floats toward the cursor; while dragging, the
  // card center tracks the cursor (offset slightly so the cursor tip sits
  // near the top-left corner like a real macOS drag).
  const liftOffProgress = interpolate(
    frame,
    [CAPTURE_FRAME, DRAG_START_FRAME],
    [0, 1],
    {
      easing: Easing.out(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );
  const selectionCenterX =
    (WHITEBOARD_TEXT_TL.x + WHITEBOARD_TEXT_BR.x) / 2;
  const selectionCenterY =
    (WHITEBOARD_TEXT_TL.y + WHITEBOARD_TEXT_BR.y) / 2;
  // While dragging, the card sits below+right of the cursor so the cursor
  // tip stays clear of the captured content (macOS-style drag preview).
  const cardLeft = lerp(
    selectionCenterX - cardW / 2,
    cursorX + 18,
    liftOffProgress,
  );
  const cardTop = lerp(
    selectionCenterY - cardH / 2,
    cursorY + 14,
    liftOffProgress,
  );

  const cursorVisible =
    frame >= CURSOR_ENTER_START_FRAME && frame <= DRAG_END_FRAME + 6;
  // Cursor click "press" tilt — slight rotation when it grabs the screenshot.
  const cursorRotation = interpolate(
    frame,
    [CAPTURE_FRAME - 2, CAPTURE_FRAME + 4, DRAG_END_FRAME],
    [0, -8, 0],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {selectionVisible && (
        <div
          style={{
            position: "absolute",
            left: WHITEBOARD_TEXT_TL.x,
            top: WHITEBOARD_TEXT_TL.y,
            width: Math.max(0, selX2 - WHITEBOARD_TEXT_TL.x),
            height: Math.max(0, selY2 - WHITEBOARD_TEXT_TL.y),
            border: "2px dashed #4a90e2",
            backgroundColor: "rgba(74,144,226,0.12)",
            boxSizing: "border-box",
          }}
        />
      )}

      {captureFlash > 0 && (
        <div
          style={{
            position: "absolute",
            left: WHITEBOARD_TEXT_TL.x,
            top: WHITEBOARD_TEXT_TL.y,
            width: WHITEBOARD_TEXT_BR.x - WHITEBOARD_TEXT_TL.x,
            height: WHITEBOARD_TEXT_BR.y - WHITEBOARD_TEXT_TL.y,
            backgroundColor: `rgba(255,255,255,${captureFlash})`,
            mixBlendMode: "screen",
          }}
        />
      )}

      {screenshotVisible && (
        <ScreenshotCard
          left={cardLeft}
          top={cardTop}
          width={cardW}
          height={cardH}
          opacity={screenshotOpacity}
        />
      )}

      {cursorVisible && (
        <Cursor x={cursorX} y={cursorY} rotationDeg={cursorRotation} />
      )}
    </AbsoluteFill>
  );
};
